import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { CONVERSATION_TITLE_MAX_LENGTH, ConversationDTO } from "@shaarp/common";
import { FormEvent, useState } from "react";
import { ButtonLoading } from "../button-loading";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToastError } from "../ui/use-toast";

export function DialogRenameConversation({
  conversation,
  onClose,
  onRenamed = () => {},
}: {
  conversation: ConversationDTO;
  onClose: () => void;
  onRenamed?: (conversation: ConversationDTO) => void;
}) {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [title, setTitle] = useState<string>(conversation.title ?? "");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    setIsLoading(true);
    try {
      const { data } = await api.patch<ConversationDTO>(
        `/conversation/${conversation.id}/rename`,
        { title: trimmedTitle }
      );
      onRenamed(data);
      onClose();
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("chat.conversation.rename.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="conversation-title">
              {t("chat.conversation.rename.label")}
            </Label>
            <Input
              id="conversation-title"
              type="text"
              value={title}
              maxLength={CONVERSATION_TITLE_MAX_LENGTH}
              placeholder={t("chat.conversation.rename.placeholder")}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
            <p className="text-xs text-right text-gray-500">
              {title.length}/{CONVERSATION_TITLE_MAX_LENGTH}
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button">
                {t("chat.conversation.rename.cancel")}
              </Button>
            </DialogClose>
            <ButtonLoading
              type="submit"
              isLoading={isLoading}
              disabled={isLoading || !title.trim()}
            >
              {t("chat.conversation.rename.save")}
            </ButtonLoading>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
